import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { editCampaignLabels } from 'api/CampaignAPI';
import { selectProjectId } from 'modules/campaignAnalytics';
import { ToastMessageList } from 'modules/campaigns/lib/components/ToastMessageList';
import { CampaignLabel } from 'modules/domains/campaign';
import { addNegativeToastWithErrorId, addPositiveToast } from 'modules/toastContainer';
import { TAG_SIZES, TagGroup } from 'ui/tags';

import { useCampaignContext } from '../../../context';
import { getLabelsSuccessMessage } from '../CampaignEditorHeader.utils';

export const EditableCampaignLabels = () => {
  const projectId = useSelector(selectProjectId);
  const dispatch = useDispatch();

  const {
    refetch: refetchCampaign,
    campaign: { id, labels },
    campaignProjectLabels,
    refetchCampaignProjectLabels,
  } = useCampaignContext();

  const handleUpdate = useCallback(
    async (updatedTags: string[]) => {
      if (updatedTags.length === 0 && labels.length === 0) return;

      const toast = {
        success: [
          'Update Success',
          getLabelsSuccessMessage(labels.length + 1, updatedTags.length + 1),
        ],
        fail: ['Unable to Update', 'Could not save Campaign Label'],
      };

      const labelsToUpdate = campaignProjectLabels
        ?.filter((label: CampaignLabel) => updatedTags?.includes(label?.name))
        ?.map((label: CampaignLabel) => ({ ...label, projectId }));

      try {
        await editCampaignLabels(id, labelsToUpdate as unknown as CampaignLabel[]);
        dispatch(addPositiveToast(<ToastMessageList messages={toast.success} id={id} />));
      } catch (e) {
        const errorMessage = <ToastMessageList messages={toast.fail} id={id} />;
        dispatch(addNegativeToastWithErrorId(errorMessage, 'EditableCampaignLabels', e));
      }

      refetchCampaign();
      refetchCampaignProjectLabels();
    },
    [
      campaignProjectLabels,
      id,
      labels,
      dispatch,
      refetchCampaign,
      projectId,
      refetchCampaignProjectLabels,
    ],
  );

  const selectedLabelTags = labels.map((tag) => tag.name);
  const labelTags = useMemo(
    () => campaignProjectLabels?.map((label: CampaignLabel) => label.name),
    [campaignProjectLabels],
  );

  return (
    <>
      {labels && (
        <TagGroup
          tags={labelTags}
          tagSize={TAG_SIZES.LARGE}
          isEditable
          isAutoSaveEnabled
          onUpdate={handleUpdate}
          value={selectedLabelTags}
        />
      )}
    </>
  );
};
